import { useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

export interface WorkingHoursDay {
  enabled: boolean
  start: string
  end: string
}

export interface WorkingHours {
  monday: WorkingHoursDay
  tuesday: WorkingHoursDay
  wednesday: WorkingHoursDay
  thursday: WorkingHoursDay
  friday: WorkingHoursDay
  saturday: WorkingHoursDay
  sunday: WorkingHoursDay
}

export interface CalendarSettings {
  id?: string
  assistant_id: string
  booking_calendar_id?: string
  conflict_calendar_ids: string[]
  working_hours: WorkingHours
  timezone: string
  is_active: boolean
  default_event_title?: string
  appointment_duration_minutes?: number
}

export interface AvailableCalendar {
  id: string
  name: string
  description?: string
  backgroundColor?: string
  foregroundColor?: string
  primary?: boolean
  accessRole?: string
}

const DEFAULT_WORKING_HOURS: WorkingHours = {
  monday: { enabled: true, start: '09:00', end: '18:00' },
  tuesday: { enabled: true, start: '09:00', end: '18:00' },
  wednesday: { enabled: true, start: '09:00', end: '18:00' },
  thursday: { enabled: true, start: '09:00', end: '18:00' },
  friday: { enabled: true, start: '09:00', end: '18:00' },
  saturday: { enabled: false, start: '09:00', end: '13:00' },
  sunday: { enabled: false, start: '09:00', end: '13:00' }
}

interface UseAssistantCalendarSettingsReturn {
  settings: CalendarSettings | null
  availableCalendars: AvailableCalendar[]
  loading: boolean
  loadingCalendars: boolean
  error: string | null
  getSettings: (assistantId: string) => Promise<CalendarSettings | null>
  saveSettings: (assistantId: string, settings: Partial<CalendarSettings>) => Promise<boolean>
  listAvailableCalendars: (assistantId: string) => Promise<AvailableCalendar[]>
  deleteSettings: (assistantId: string) => Promise<boolean>
}

export const useAssistantCalendarSettings = (): UseAssistantCalendarSettingsReturn => {
  const { user } = useAuth()
  const [settings, setSettings] = useState<CalendarSettings | null>(null)
  const [availableCalendars, setAvailableCalendars] = useState<AvailableCalendar[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingCalendars, setLoadingCalendars] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const accountId = user?.account_id

  const getSettings = useCallback(async (assistantId: string): Promise<CalendarSettings | null> => {
    if (!accountId || !supabase) {
      setError('Conta nao identificada')
      return null
    }

    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('account_assistant_calendar_settings')
        .select('*')
        .eq('assistant_id', assistantId)
        .eq('account_id', accountId)
        .maybeSingle()

      if (fetchError) {
        console.error('[useAssistantCalendarSettings] Error fetching settings:', fetchError)
        setError('Erro ao buscar configuracoes')
        return null
      }

      if (data) {
        const calendarSettings: CalendarSettings = {
          id: data.id,
          assistant_id: data.assistant_id,
          booking_calendar_id: data.booking_calendar_id,
          conflict_calendar_ids: data.conflict_calendar_ids || [],
          working_hours: data.working_hours || DEFAULT_WORKING_HOURS,
          timezone: data.timezone || 'America/Sao_Paulo',
          is_active: data.is_active,
          default_event_title: data.default_event_title || 'Reuniao',
          appointment_duration_minutes: data.appointment_duration_minutes || 60
        }
        setSettings(calendarSettings)
        return calendarSettings
      }

      const defaultSettings: CalendarSettings = {
        assistant_id: assistantId,
        conflict_calendar_ids: [],
        working_hours: DEFAULT_WORKING_HOURS,
        timezone: 'America/Sao_Paulo',
        is_active: true,
        default_event_title: 'Reuniao',
        appointment_duration_minutes: 60
      }
      setSettings(defaultSettings)
      return defaultSettings

    } catch (err) {
      console.error('[useAssistantCalendarSettings] Unexpected error:', err)
      setError('Erro inesperado')
      return null
    } finally {
      setLoading(false)
    }
  }, [accountId])

  const saveSettings = useCallback(async (assistantId: string, newSettings: Partial<CalendarSettings>): Promise<boolean> => {
    if (!accountId || !supabase) {
      setError('Conta nao identificada')
      return false
    }

    try {
      setLoading(true)
      setError(null)

      const { data: existing } = await supabase
        .from('account_assistant_calendar_settings')
        .select('id')
        .eq('assistant_id', assistantId)
        .eq('account_id', accountId)
        .maybeSingle()

      const sanitizedTitle = (newSettings.default_event_title || '')
        .trim()
        .replace(/\s+/g, ' ')
        .replace(/[\s|]+$/g, '')
        .trim()
      const settingsData = {
        account_id: accountId,
        assistant_id: assistantId,
        booking_calendar_id: newSettings.booking_calendar_id,
        conflict_calendar_ids: newSettings.conflict_calendar_ids || [],
        working_hours: newSettings.working_hours || DEFAULT_WORKING_HOURS,
        timezone: newSettings.timezone || 'America/Sao_Paulo',
        is_active: newSettings.is_active !== undefined ? newSettings.is_active : true,
        default_event_title: sanitizedTitle || 'Reuniao',
        appointment_duration_minutes: newSettings.appointment_duration_minutes || 60
      }

      let result
      if (existing?.id) {
        result = await supabase
          .from('account_assistant_calendar_settings')
          .update(settingsData)
          .eq('id', existing.id)
          .select()
          .single()
      } else {
        result = await supabase
          .from('account_assistant_calendar_settings')
          .insert(settingsData)
          .select()
          .single()
      }

      if (result.error) {
        console.error('[useAssistantCalendarSettings] Error saving settings:', result.error)
        setError('Erro ao salvar configuracoes')
        return false
      }

      const savedSettings: CalendarSettings = {
        id: result.data.id,
        assistant_id: result.data.assistant_id,
        booking_calendar_id: result.data.booking_calendar_id,
        conflict_calendar_ids: result.data.conflict_calendar_ids || [],
        working_hours: result.data.working_hours || DEFAULT_WORKING_HOURS,
        timezone: result.data.timezone || 'America/Sao_Paulo',
        is_active: result.data.is_active,
        default_event_title: result.data.default_event_title || 'Reuniao',
        appointment_duration_minutes: result.data.appointment_duration_minutes || 60
      }
      setSettings(savedSettings)
      return true

    } catch (err) {
      console.error('[useAssistantCalendarSettings] Unexpected error:', err)
      setError('Erro inesperado')
      return false
    } finally {
      setLoading(false)
    }
  }, [accountId])

  const listAvailableCalendars = useCallback(async (assistantId: string): Promise<AvailableCalendar[]> => {
    if (!accountId || !supabase) {
      setError('Conta nao identificada')
      return []
    }

    if (!assistantId) {
      setAvailableCalendars([])
      return []
    }

    try {
      setLoadingCalendars(true)
      setError(null)

      const { data: { session } } = await supabase.auth.getSession()

      if (!session?.access_token) {
        setError('Sessao expirada')
        return []
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const response = await fetch(`${supabaseUrl}/functions/v1/google-calendar-list-calendars`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ account_id: accountId, assistant_id: assistantId })
      })

      const result = await response.json()

      if (!response.ok) {
        if (result.code === 'NOT_CONNECTED') {
          setAvailableCalendars([])
          return []
        }
        setError(result.error || 'Erro ao listar agendas')
        return []
      }

      const calendars = result.calendars || []
      setAvailableCalendars(calendars)
      return calendars

    } catch (err) {
      console.error('[useAssistantCalendarSettings] Error listing calendars:', err)
      setError('Erro ao listar agendas')
      return []
    } finally {
      setLoadingCalendars(false)
    }
  }, [accountId])

  const deleteSettings = useCallback(async (assistantId: string): Promise<boolean> => {
    if (!accountId || !supabase) {
      setError('Conta nao identificada')
      return false
    }

    try {
      setLoading(true)
      setError(null)

      const { error: deleteError } = await supabase
        .from('account_assistant_calendar_settings')
        .delete()
        .eq('assistant_id', assistantId)
        .eq('account_id', accountId)

      if (deleteError) {
        console.error('[useAssistantCalendarSettings] Error deleting settings:', deleteError)
        setError('Erro ao remover configuracoes')
        return false
      }

      setSettings(null)
      return true

    } catch (err) {
      console.error('[useAssistantCalendarSettings] Unexpected error:', err)
      setError('Erro inesperado')
      return false
    } finally {
      setLoading(false)
    }
  }, [accountId])

  return {
    settings,
    availableCalendars,
    loading,
    loadingCalendars,
    error,
    getSettings,
    saveSettings,
    listAvailableCalendars,
    deleteSettings
  }
}
